/**
 * ThemePicker — sits at the bottom of the sidebar.
 * Shows a palette icon button; clicking opens a popover with
 * preset theme swatches and a custom accent color input.
 */
import { useState, useRef, useEffect } from 'react'
import { useThemeStore, PRESETS } from '../../stores/theme'

export default function ThemePicker() {
  const { theme, customAccent, setPreset, setCustomAccent } = useThemeStore()
  const [open, setOpen] = useState(false)
  const [hexInput, setHexInput] = useState(customAccent || theme.accent)
  const modalRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  function handleHexChange(val: string) {
    setHexInput(val)
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      setCustomAccent(val)
    }
  }

  return (
    <>
      {/* Palette trigger button — stays in the sidebar */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Customize theme"
        style={{
          width: 40, height: 40, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', border: 'none',
          background: open ? 'var(--bg-elevated)' : 'transparent',
          flexShrink: 0,
          boxShadow: open ? '0 0 0 2px var(--accent)' : 'none',
          transition: 'box-shadow 0.2s',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="var(--text-muted)" strokeWidth="1.5"/>
          <circle cx="8.5" cy="10" r="1.5" fill="var(--accent)"/>
          <circle cx="15.5" cy="10" r="1.5" fill="#3b82f6"/>
          <circle cx="12" cy="15.5" r="1.5" fill="#22c55e"/>
          <circle cx="8.5" cy="15" r="1.5" fill="#ef4444"/>
          <circle cx="15.5" cy="15" r="1.5" fill="#a855f7"/>
        </svg>
      </button>

      {/* Bottom-left modal */}
      {open && (
        <div
          ref={modalRef}
          style={{
            position: 'fixed',
            bottom: 16,
            left: 64,   // just to the right of the 52px sidebar
            width: 260,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: 18,
            zIndex: 9999,
            boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              🎨 Theme
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>

          {/* Preset swatches — 3×2 grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
            {PRESETS.map(p => (
              <button
                key={p.id}
                onClick={() => { setPreset(p.id); setHexInput(customAccent || p.accent) }}
                title={p.label}
                style={{
                  background: p.bgSurface,
                  border: `2px solid ${theme.id === p.id ? p.accent : p.border}`,
                  borderRadius: 10,
                  padding: '10px 6px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'border-color 0.15s, transform 0.15s',
                  transform: theme.id === p.id ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: p.accent, boxShadow: `0 2px 8px ${p.accent}60` }} />
                <div style={{ fontSize: 9, color: p.textMuted, fontWeight: 700, letterSpacing: 0.3 }}>{p.label.toUpperCase()}</div>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--border)', marginBottom: 14 }} />

          {/* Custom accent */}
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
            Custom Accent Color
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: customAccent ? 8 : 0 }}>
            <input
              type="color"
              value={hexInput.match(/^#[0-9A-Fa-f]{6}$/) ? hexInput : theme.accent}
              onChange={e => handleHexChange(e.target.value)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '2px solid var(--border)',
                cursor: 'pointer', padding: 2, background: 'var(--bg-elevated)', flexShrink: 0,
              }}
            />
            <input
              type="text"
              value={hexInput}
              onChange={e => handleHexChange(e.target.value)}
              placeholder="#c9a84c"
              maxLength={7}
              style={{
                flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '7px 10px', color: 'var(--text-primary)',
                fontSize: 13, outline: 'none', fontFamily: 'monospace',
              }}
            />
          </div>

          {customAccent && (
            <button
              onClick={() => {
                const base = PRESETS.find(p => p.id === theme.id)?.accent ?? '#c9a84c'
                setHexInput(base)
                setCustomAccent(base)
              }}
              style={{
                width: '100%', padding: '6px 0',
                background: 'none', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-muted)', fontSize: 11,
                cursor: 'pointer', marginTop: 2,
              }}
            >
              Reset to preset accent
            </button>
          )}
        </div>
      )}
    </>
  )
}
